import React from "react";
import styled from "styled-components";
import { apiPost } from "../api";
import { brand_orange_deep, brand_success, dark } from "../constants/styles";

// Email / WhatsApp channel picker for the CheckInbox page. Email posts to
// the existing `send_confirmation` endpoint (which enqueues the existing
// `Emails::RegistrationConfirmationJob`). WhatsApp is rendered as a disabled
// affordance with a "Segera hadir" badge so the UI signals the future
// capability without promising it. Flip `waEnabled` true when the WA
// integration ships; the button re-enables and posts with `type=whatsApp`.

const RESEND_COOLDOWN_SEC = 60;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const OptionButton = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1px solid
    ${({ $disabled }) => ($disabled ? "rgba(0,0,0,0.06)" : "#d1d5db")};
  border-radius: 14px;
  background: ${({ $disabled }) => ($disabled ? "#f9fafb" : "#ffffff")};
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition:
    border-color 150ms ease,
    background 150ms ease,
    transform 120ms ease,
    box-shadow 150ms ease;
  width: 100%;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: ${brand_orange_deep};
    box-shadow: 0 6px 16px rgba(216, 89, 7, 0.12);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 3px;
  }

  [data-theme="dark"] & {
    background: ${({ $disabled }) =>
      $disabled ? "rgba(255,255,255,0.03)" : dark.surface_bg};
    border-color: ${({ $disabled }) =>
      $disabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.1)"};
  }
`;

const Icon = styled.span`
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const EmailIcon = styled(Icon)`
  background: rgba(216, 89, 7, 0.12);
  color: ${brand_orange_deep};
`;

const WAIcon = styled(Icon)`
  background: rgba(37, 211, 102, 0.14);
  color: #16a34a;
`;

const OptionBody = styled.div`
  flex: 1 1 auto;
`;

const OptionTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #1f2937;
  margin-bottom: 2px;

  [data-theme="dark"] & {
    color: ${dark.text_primary};
  }
`;

const OptionSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;

  [data-theme="dark"] & {
    color: ${dark.text_secondary};
  }
`;

const Pill = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
  background: ${({ $tone }) =>
    $tone === "success" ? "rgba(22,163,74,0.14)" : "rgba(107,114,128,0.12)"};
  color: ${({ $tone }) => ($tone === "success" ? brand_success : "#6b7280")};
`;

const Feedback = styled.div`
  font-size: 12px;
  color: ${brand_success};
  padding-left: 4px;
`;

export default function ChannelPicker({ email, resendEmailPath, waEnabled }) {
  const [sending, setSending] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleEmailSend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    try {
      await apiPost(resendEmailPath, { body: {} });
      setSent(true);
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      if (typeof console !== "undefined") {
        console.warn("[ChannelPicker] resend email failed", err);
      }
      setError("Gagal mengirim email. Coba lagi sebentar.");
    } finally {
      setSending(false);
    }
  };

  const emailDisabled = sending || cooldown > 0;

  return (
    <List>
      <OptionButton
        type="button"
        $disabled={emailDisabled}
        disabled={emailDisabled}
        onClick={handleEmailSend}
        aria-label="Kirim tautan konfirmasi melalui email"
      >
        <EmailIcon aria-hidden="true">✉</EmailIcon>
        <OptionBody>
          <OptionTitle>Email</OptionTitle>
          <OptionSubtitle>
            {sending && "Mengirim ke " + email + "…"}
            {!sending &&
              cooldown > 0 &&
              `Tunggu ${cooldown} detik sebelum kirim ulang`}
            {!sending &&
              cooldown === 0 &&
              (sent ? "Kirim ulang ke " + email : "Kirim ke " + email)}
          </OptionSubtitle>
        </OptionBody>
        {sent && cooldown > 0 && <Pill $tone="success">Terkirim</Pill>}
      </OptionButton>

      <OptionButton
        type="button"
        $disabled={!waEnabled}
        disabled={!waEnabled}
        aria-label="Kirim tautan konfirmasi melalui WhatsApp — segera hadir"
      >
        <WAIcon aria-hidden="true">💬</WAIcon>
        <OptionBody>
          <OptionTitle>WhatsApp</OptionTitle>
          <OptionSubtitle>
            {waEnabled
              ? "Kirim ke nomor WhatsApp terdaftar"
              : "Metode pengiriman baru — belum tersedia"}
          </OptionSubtitle>
        </OptionBody>
        {!waEnabled && <Pill>Segera hadir</Pill>}
      </OptionButton>

      {error && <Feedback style={{ color: "#b91c1c" }}>{error}</Feedback>}
    </List>
  );
}
